import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit, FiTrash2, FiX, FiImage, FiSearch, FiFilter, FiCheck, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const API_URL = process.env.REACT_APP_API_URL || 'https://green-admin.merket.io/api';
const ITEMS_PER_PAGE = 20;

function MenuManagement() {
  // Tab state
  const [activeTab, setActiveTab] = useState('products');
  
  // Mobile states
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showFilters, setShowFilters] = useState(false);
  
  // Products state
  const [menuItems, setMenuItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '', description: '', price: '', category: '', available: true, hasHotOption: false
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [customOptions, setCustomOptions] = useState([]);
  const [newOption, setNewOption] = useState({ name: '', price: '' });
  const [selectedToppings, setSelectedToppings] = useState([]);
  
  // Bulk selection state
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showBulkToppingModal, setShowBulkToppingModal] = useState(false);
  const [bulkToppings, setBulkToppings] = useState([]);
  
  // Bulk edit state
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkEditForm, setBulkEditForm] = useState({
    description: '',
    category: '',
    price: '',
    available: '',
    hasHotOption: '',
    toppingAction: '', // '', 'replace', 'add', 'remove'
    selectedToppings: []
  });
  
  // Toppings state
  const [toppings, setToppings] = useState([]);
  const [showToppingModal, setShowToppingModal] = useState(false);
  const [editingTopping, setEditingTopping] = useState(null);
  const [toppingForm, setToppingForm] = useState({ name: '', price: '' });
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Pagination states
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  
  const observerTarget = useRef(null);
  const searchDebounceRef = useRef(null);

  // Debounce search term
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchTerm]);

  // Load menu items with server-side pagination
  const loadMenuItems = useCallback(async (pageNum = 1, reset = false) => {
    try {
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const params = {
        page: pageNum,
        limit: ITEMS_PER_PAGE,
        skipCount: 'true'
      };

      if (debouncedSearch) params.search = debouncedSearch;
      if (filterCategory !== 'all') params.category = filterCategory;
      if (filterStatus !== 'all') {
        params.status = filterStatus === 'available' ? 'active' : 'inactive';
      }

      const response = await axios.get(`${API_URL}/menu`, { params });
      const { data, pagination } = response.data;

      if (reset || pageNum === 1) {
        setMenuItems(data || []);
      } else {
        setMenuItems(prev => [...prev, ...(data || [])]);
      }

      setTotalItems(pagination?.total || (data || []).length);
      setPage(pageNum);
      setHasMore(pagination?.hasMore || false);
    } catch (error) {
      toast.error('Không thể tải menu');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [debouncedSearch, filterCategory, filterStatus]);

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/categories`);
      if (response.data.data && response.data.data.length > 0) {
        setCategories(response.data.data.filter(cat => cat.active));
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadToppings = async () => {
    try {
      const response = await axios.get(`${API_URL}/toppings`);
      setToppings(response.data.data || []);
    } catch (error) {
      console.error('Error loading toppings:', error);
    }
  };
  
  // Load more items when scrolling
  const loadMoreItems = useCallback(() => {
    if (!hasMore || loadingMore) return;
    loadMenuItems(page + 1, false);
  }, [hasMore, loadingMore, page, loadMenuItems]);

  // Now define useEffects
  useEffect(() => {
    loadCategories();
    loadToppings();
    
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Reload when filters change (loadMenuItems changes when debouncedSearch, filterCategory, filterStatus change)
  useEffect(() => {
    loadMenuItems(1, true);
  }, [loadMenuItems]);
  
  useEffect(() => {
    const target = observerTarget.current;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMoreItems();
        }
      },
      { threshold: 0.1 }
    );

    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [hasMore, loadingMore, loadMoreItems]);
  
  useEffect(() => {
    setPage(1);
  }, [searchTerm, filterCategory, filterStatus]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const addCustomOption = () => {
    if (newOption.name && newOption.price) {
      setCustomOptions([...customOptions, { name: newOption.name, price: parseInt(newOption.price, 10) || 0 }]);
      setNewOption({ name: '', price: '' });
    }
  };

  const removeCustomOption = (index) => {
    setCustomOptions(customOptions.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('description', formData.description);
      submitData.append('price', formData.price);
      submitData.append('category', formData.category);
      submitData.append('available', formData.available);
      submitData.append('hasHotOption', formData.hasHotOption);
      submitData.append('customOptions', JSON.stringify(customOptions));
      submitData.append('availableToppings', JSON.stringify(selectedToppings));
      
      if (imageFile) {
        submitData.append('image', imageFile);
      }
      
      if (editingItem) {
        await axios.put(`${API_URL}/menu/${editingItem._id}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axios.post(`${API_URL}/menu`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      loadMenuItems(1, true);
      closeModal();
    } catch (error) {
      console.error('Save menu item error:', error);
      toast.error(error.response?.data?.message || 'Lỗi khi lưu món');
    }
  };

  const deleteItem = async (id) => {
    if (window.confirm('Xóa món này?')) {
      try {
        await axios.delete(`${API_URL}/menu/${id}`);
        loadMenuItems(1, true);
      } catch (error) {
        toast.error('Không thể xóa món');
      }
    }
  };

  const openModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name,
        description: item.description,
        price: item.price,
        category: item.category,
        available: item.available,
        hasHotOption: item.hasHotOption || false
      });
      setCustomOptions(item.customOptions || []);
      setSelectedToppings(item.availableToppings?.map(t => t._id || t) || []);
      if (item.image) {
        setImagePreview(`${API_URL.replace('/api', '')}${item.image}`);
      }
    } else {
      setEditingItem(null);
      setFormData({ name: '', description: '', price: '', category: categories[0]?.name || '', available: true, hasHotOption: false });
      setCustomOptions([]);
      setSelectedToppings([]);
      setImagePreview(null);
    }
    setImageFile(null);
    setNewOption({ name: '', price: '' });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setImageFile(null);
    setImagePreview(null);
    setCustomOptions([]);
    setSelectedToppings([]);
    setNewOption({ name: '', price: '' });
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(Math.round(amount || 0));

  // Topping functions
  const openToppingModal = (topping = null) => {
    if (topping) {
      setEditingTopping(topping);
      setToppingForm({ name: topping.name, price: topping.price });
    } else {
      setEditingTopping(null);
      setToppingForm({ name: '', price: '' });
    }
    setShowToppingModal(true);
  };

  const closeToppingModal = () => {
    setShowToppingModal(false);
    setEditingTopping(null);
    setToppingForm({ name: '', price: '' });
  };

  const handleToppingSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTopping) {
        await axios.put(`${API_URL}/toppings/${editingTopping._id}`, toppingForm);
      } else {
        await axios.post(`${API_URL}/toppings`, toppingForm);
      }
      loadToppings();
      closeToppingModal();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Lỗi khi lưu topping');
    }
  };

  const deleteTopping = async (id) => {
    if (window.confirm('Xóa topping này?')) {
      try {
        await axios.delete(`${API_URL}/toppings/${id}`);
        loadToppings();
      } catch (error) {
        toast.error('Không thể xóa topping');
      }
    }
  };

  const toggleToppingActive = async (topping) => {
    try {
      await axios.put(`${API_URL}/toppings/${topping._id}`, { ...topping, active: !topping.active });
      loadToppings();
    } catch (error) {
      toast.error('Không thể cập nhật trạng thái');
    }
  };

  const toggleToppingStock = async (topping) => {
    try {
      await axios.patch(`${API_URL}/toppings/${topping._id}/toggle-stock`);
      toast.success(topping.inStock ? 'Đã đánh dấu hết hàng' : 'Đã đánh dấu còn hàng');
      loadToppings();
    } catch (error) {
      toast.error('Không thể cập nhật tình trạng kho');
    }
  };

  const toggleToppingSelection = (toppingId) => {
    if (selectedToppings.includes(toppingId)) {
      setSelectedToppings(selectedToppings.filter(id => id !== toppingId));
    } else {
      setSelectedToppings([...selectedToppings, toppingId]);
    }
  };

  // Bulk selection functions
  const toggleProductSelection = (productId) => {
    if (selectedProducts.includes(productId)) {
      setSelectedProducts(selectedProducts.filter(id => id !== productId));
    } else {
      setSelectedProducts([...selectedProducts, productId]);
    }
  };

  const toggleSelectAll = () => {
    if (selectedProducts.length === menuItems.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(menuItems.map(item => item._id));
    }
  };

  const clearSelection = () => {
    setSelectedProducts([]);
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (selectedProducts.length === 0) {
      toast.warning('Chưa chọn sản phẩm nào');
      return;
    }
    
    if (window.confirm(`Xóa ${selectedProducts.length} sản phẩm đã chọn?`)) {
      try {
        let successCount = 0;
        let failCount = 0;
        
        for (const productId of selectedProducts) {
          try {
            await axios.delete(`${API_URL}/menu/${productId}`);
            successCount++;
          } catch (error) {
            failCount++;
          }
        }
        
        if (failCount > 0) {
          toast.error(`Không thể xóa ${failCount} sản phẩm`);
        }
        
        loadMenuItems(1, true);
        setSelectedProducts([]);
      } catch (error) {
        toast.error('Lỗi khi xóa sản phẩm');
      }
    }
  };

  // Bulk add toppings
  const openBulkToppingModal = () => {
    if (selectedProducts.length === 0) {
      toast.warning('Chưa chọn sản phẩm nào');
      return;
    }
    setBulkToppings([]);
    setShowBulkToppingModal(true);
  };

  const closeBulkToppingModal = () => {
    setShowBulkToppingModal(false);
    setBulkToppings([]);
  };

  const toggleBulkTopping = (toppingId) => {
    if (bulkToppings.includes(toppingId)) {
      setBulkToppings(bulkToppings.filter(id => id !== toppingId));
    } else {
      setBulkToppings([...bulkToppings, toppingId]);
    }
  };

  const handleBulkAddToppings = async () => {
    if (bulkToppings.length === 0) {
      toast.warning('Chưa chọn topping nào');
      return;
    }

    try {
      let successCount = 0;
      let failCount = 0;

      for (const productId of selectedProducts) {
        try {
          // Get current product
          const product = menuItems.find(item => item._id === productId);
          if (!product) continue;

          // Merge existing toppings with new toppings (avoid duplicates)
          const existingToppingIds = (product.availableToppings || []).map(t => t._id || t);
          const newToppingIds = [...new Set([...existingToppingIds, ...bulkToppings])];

          // Update product
          const submitData = new FormData();
          submitData.append('name', product.name);
          submitData.append('description', product.description || '');
          submitData.append('price', product.price);
          submitData.append('category', product.category);
          submitData.append('available', product.available);
          submitData.append('hasHotOption', product.hasHotOption || false);
          submitData.append('customOptions', JSON.stringify(product.customOptions || []));
          submitData.append('availableToppings', JSON.stringify(newToppingIds));

          await axios.put(`${API_URL}/menu/${productId}`, submitData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          successCount++;
        } catch (error) {
          failCount++;
        }
      }

      if (failCount > 0) {
        toast.error(`Không thể cập nhật ${failCount} sản phẩm`);
      }

      loadMenuItems(1, true);
      setSelectedProducts([]);
      closeBulkToppingModal();
    } catch (error) {
      toast.error('Lỗi khi thêm topping');
    }
  };

  // Bulk edit functions
  const openBulkEditModal = () => {
    if (selectedProducts.length === 0) {
      toast.warning('Chưa chọn sản phẩm nào');
      return;
    }
    setBulkEditForm({
      description: '',
      category: '',
      price: '',
      available: '',
      hasHotOption: '',
      toppingAction: '',
      selectedToppings: []
    });
    setShowBulkEditModal(true);
  };

  const closeBulkEditModal = () => {
    setShowBulkEditModal(false);
    setBulkEditForm({
      description: '',
      category: '',
      price: '',
      available: '',
      hasHotOption: '',
      toppingAction: '',
      selectedToppings: []
    });
  };

  const toggleBulkEditTopping = (toppingId) => {
    if (bulkEditForm.selectedToppings.includes(toppingId)) {
      setBulkEditForm({
        ...bulkEditForm,
        selectedToppings: bulkEditForm.selectedToppings.filter(id => id !== toppingId)
      });
    } else {
      setBulkEditForm({
        ...bulkEditForm,
        selectedToppings: [...bulkEditForm.selectedToppings, toppingId]
      });
    }
  };

  const handleBulkEdit = async () => {
    // Check if any field is set
    const hasChanges = bulkEditForm.description ||
                       bulkEditForm.category || 
                       bulkEditForm.price || 
                       bulkEditForm.available || 
                       bulkEditForm.hasHotOption ||
                       bulkEditForm.toppingAction;
    
    if (!hasChanges) {
      toast.warning('Chưa có thay đổi nào');
      return;
    }

    try {
      let successCount = 0;
      let failCount = 0;

      for (const productId of selectedProducts) {
        try {
          const product = menuItems.find(item => item._id === productId);
          if (!product) continue;

          // Build update data - keep existing values if not changed
          const submitData = new FormData();
          submitData.append('name', product.name);
          submitData.append('description', bulkEditForm.description || product.description || '');
          submitData.append('price', bulkEditForm.price || product.price);
          submitData.append('category', bulkEditForm.category || product.category);
          submitData.append('available', bulkEditForm.available !== '' ? bulkEditForm.available : product.available);
          submitData.append('hasHotOption', bulkEditForm.hasHotOption !== '' ? bulkEditForm.hasHotOption : (product.hasHotOption || false));
          submitData.append('customOptions', JSON.stringify(product.customOptions || []));

          // Handle toppings based on action
          let finalToppings = (product.availableToppings || []).map(t => t._id || t);
          
          if (bulkEditForm.toppingAction === 'replace' && bulkEditForm.selectedToppings.length > 0) {
            // Replace all toppings
            finalToppings = bulkEditForm.selectedToppings;
          } else if (bulkEditForm.toppingAction === 'add' && bulkEditForm.selectedToppings.length > 0) {
            // Add to existing (avoid duplicates)
            finalToppings = [...new Set([...finalToppings, ...bulkEditForm.selectedToppings])];
          } else if (bulkEditForm.toppingAction === 'remove') {
            // Remove all toppings
            finalToppings = [];
          }
          
          submitData.append('availableToppings', JSON.stringify(finalToppings));

          await axios.put(`${API_URL}/menu/${productId}`, submitData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          successCount++;
        } catch (error) {
          console.error('Error updating product:', error);
          failCount++;
        }
      }

      if (failCount > 0) {
        toast.error(`Không thể cập nhật ${failCount} sản phẩm`);
      }

      loadMenuItems(1, true);
      setSelectedProducts([]);
      closeBulkEditModal();
    } catch (error) {
      toast.error('Lỗi khi cập nhật sản phẩm');
    }
  };

  // Long press for bulk select
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const longPressTimer = useRef(null);
  const isLongPress = useRef(false);

  const handleTouchStart = (e, itemId) => {
    isLongPress.current = false;
    
    // Start long press timer (800ms)
    longPressTimer.current = setTimeout(() => {
      isLongPress.current = true;
      setBulkSelectMode(true);
      toggleProductSelection(itemId);
      if (navigator.vibrate) navigator.vibrate(50);
    }, 800);
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleTouchMove = () => {
    // Cancel long press if user moves finger
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const exitBulkSelectMode = () => {
    setBulkSelectMode(false);
    setSelectedProducts([]);
  };

  // Mobile Product Card Component with long press for bulk select
  const MobileProductCard = ({ item }) => {
    const isSelected = selectedProducts.includes(item._id);
    
    return (
      <div 
        className={`bg-white flex items-center p-3 active:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}
        onTouchStart={(e) => handleTouchStart(e, item._id)}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
        onClick={() => {
          if (isLongPress.current) return;
          if (bulkSelectMode) {
            toggleProductSelection(item._id);
          } else {
            openModal(item);
          }
        }}
      >
        {/* Checkbox for bulk select */}
        {bulkSelectMode && (
          <div className="mr-3">
            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-green-500 border-green-500' : 'border-gray-300 bg-white'}`}>
              {isSelected && <FiCheck className="text-white" size={14} />}
            </div>
          </div>
        )}
        
        {/* Image */}
        <img 
          src={item.image ? `${API_URL.replace('/api', '')}${item.image}` : '/default-product.png'} 
          alt={item.name} 
          className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
        />
        
        {/* Info */}
        <div className="ml-3 flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p className="font-medium text-sm text-gray-900 truncate">{item.name}</p>
            {!item.available && (
              <span className="ml-2 px-1.5 py-0.5 bg-red-100 text-red-600 text-xs rounded flex-shrink-0">Hết</span>
            )}
          </div>
          <p className="text-sm font-semibold text-green-600 mt-0.5">{formatCurrency(item.price)}</p>
        </div>
        
        {/* Arrow indicator */}
        {!bulkSelectMode && <FiChevronRight className="text-gray-300 ml-2" size={20} />}
      </div>
    );
  };

  // Mobile Topping Card Component - Simple click to edit
  const MobileToppingCard = ({ topping }) => {
    return (
      <div 
        className="bg-white flex items-center justify-between p-3 active:bg-gray-50"
        onClick={() => openToppingModal(topping)}
      >
        <div className="flex items-center flex-1 min-w-0">
          <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-sm">🧋</span>
          </div>
          <div className="ml-3 flex-1 min-w-0">
            <p className="font-medium text-sm text-gray-900 truncate">{topping.name}</p>
            <p className="text-sm font-semibold text-green-600">{formatCurrency(topping.price)}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2 ml-2">
          {!topping.active && (
            <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">Tắt</span>
          )}
          {topping.inStock === false && (
            <span className="px-1.5 py-0.5 bg-orange-100 text-orange-600 text-xs rounded">Hết</span>
          )}
          <FiChevronRight className="text-gray-300" size={18} />
        </div>
      </div>
    );
  };

  return (
    <div className={isMobile ? "-m-4 bg-gray-50 pb-4" : "space-y-6"}>
      {/* Header */}
      <div className={isMobile ? "bg-white px-4 py-3 flex items-center justify-between shadow-sm" : "flex justify-between items-center"}>
        <h1 className={isMobile ? "text-lg font-bold text-gray-800" : "text-3xl font-bold"}>Quản lý sản phẩm</h1>
        {isMobile && (
          <button 
            onClick={() => openModal()} 
            className="w-9 h-9 bg-green-500 text-white rounded-full flex items-center justify-center"
          >
            <FiPlus size={20} />
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className={isMobile ? "bg-white border-b border-gray-200 px-4" : "border-b border-gray-200"}>
        <nav className="flex space-x-6">
          <button
            onClick={() => setActiveTab('products')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'products'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500'
            }`}
          >
            🍽️ Sản phẩm
          </button>
          <button
            onClick={() => setActiveTab('toppings')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'toppings'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500'
            }`}
          >
            🧋 Topping
          </button>
        </nav>
      </div>

      {/* Products Tab */}
      {activeTab === 'products' && (
        <>
          {/* Desktop: Add button */}
          {!isMobile && (
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                {selectedProducts.length > 0 && (
                  <span className="text-sm text-gray-600">
                    Đã chọn {selectedProducts.length} sản phẩm
                  </span>
                )}
              </div>
              <button onClick={() => openModal()} className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                <FiPlus /><span>Thêm món</span>
              </button>
            </div>
          )}

          {/* Mobile: Bulk Select Mode Header */}
          {isMobile && bulkSelectMode && (
            <div className="bg-blue-500 text-white px-4 py-3 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <button onClick={exitBulkSelectMode} className="p-1">
                  <FiX size={20} />
                </button>
                <span className="font-medium">{selectedProducts.length} đã chọn</span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    handleBulkDelete();
                    exitBulkSelectMode();
                  }}
                  disabled={selectedProducts.length === 0}
                  className="p-2 rounded-full bg-white/20 disabled:opacity-50"
                >
                  <FiTrash2 size={18} />
                </button>
                <button
                  onClick={openBulkEditModal}
                  disabled={selectedProducts.length === 0}
                  className="p-2 rounded-full bg-white/20 disabled:opacity-50"
                >
                  <FiEdit size={18} />
                </button>
              </div>
            </div>
          )}

          {/* Desktop: Bulk Action Bar */}
          {!isMobile && selectedProducts.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="font-semibold text-blue-800">
                  ✓ Đã chọn {selectedProducts.length} sản phẩm
                </span>
                <button
                  onClick={clearSelection}
                  className="text-blue-600 hover:text-blue-800 text-sm underline"
                >
                  Bỏ chọn tất cả
                </button>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={openBulkEditModal}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <FiEdit /><span>Sửa hàng loạt</span>
                </button>
                <button
                  onClick={openBulkToppingModal}
                  className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  <span>🧋 Thêm topping</span>
                </button>
                <button
                  onClick={handleBulkDelete}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  <FiTrash2 /><span>Xóa</span>
                </button>
              </div>
            </div>
          )}

          {/* Search and Filters */}
          {isMobile ? (
            // Mobile: Compact search with filter icon and add button
            <div className="bg-white px-4 py-3 flex items-center space-x-2">
              <div className="relative flex-1">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Tìm kiếm..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg"
                />
              </div>
              <button 
                onClick={() => setShowFilters(!showFilters)}
                className={`p-2 rounded-lg border ${showFilters || filterCategory !== 'all' || filterStatus !== 'all' ? 'bg-green-50 border-green-500 text-green-600' : 'border-gray-300 text-gray-500'}`}
              >
                <FiFilter size={18} />
              </button>
              <button 
                onClick={() => openModal()}
                className="p-2 rounded-lg bg-green-600 text-white hover:bg-green-700"
              >
                <FiPlus size={18} />
              </button>
            </div>
          ) : (
            // Desktop: Full filters
            <div className="bg-white rounded-lg shadow p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Tìm kiếm tên món..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg"
                  />
                </div>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="border rounded-lg px-4 py-2"
                >
                  <option value="all">Tất cả danh mục</option>
                  {categories.map(cat => (
                    <option key={cat._id} value={cat.name}>{cat.icon} {cat.name}</option>
                  ))}
                </select>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="border rounded-lg px-4 py-2"
                >
                  <option value="all">Tất cả trạng thái</option>
                  <option value="available">Còn món</option>
                  <option value="unavailable">Hết món</option>
                </select>
              </div>
            </div>
          )}

          {/* Mobile: Filter dropdown */}
          {isMobile && showFilters && (
            <div className="bg-white px-4 py-3 border-t space-y-3">
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="all">Tất cả danh mục</option>
                {categories.map(cat => (
                  <option key={cat._id} value={cat.name}>{cat.icon} {cat.name}</option>
                ))}
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="all">Tất cả trạng thái</option>
                <option value="available">Còn món</option>
                <option value="unavailable">Hết món</option>
              </select>
            </div>
          )}

          {/* Mobile: Long press hint */}
          {isMobile && !bulkSelectMode && menuItems.length > 0 && (
            <div className="px-4 py-2 text-xs text-gray-500 text-center">
              Giữ lâu để chọn nhiều sản phẩm
            </div>
          )}

          {/* Products List/Table */}
          {isMobile ? (
            // Mobile: Card list
            <div className="divide-y divide-gray-100">
              {menuItems.map((item) => (
                <MobileProductCard key={item._id} item={item} />
              ))}
              {loadingMore && (
                <div className="text-center py-4 text-gray-500 text-sm">
                  <div className="inline-block animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-green-500 mr-2"></div>
                  Đang tải thêm...
                </div>
              )}
              {!loadingMore && hasMore && (
                <div ref={observerTarget} className="text-center py-2 text-gray-400 text-xs">
                  Cuộn xuống để tải thêm ({menuItems.length} món đã tải)
                </div>
              )}
              {menuItems.length === 0 && !loading && (
                <div className="text-center py-12 text-gray-500 text-sm">
                  {searchTerm || filterCategory !== 'all' || filterStatus !== 'all' ? 'Không tìm thấy món ăn phù hợp' : 'Chưa có món ăn nào'}
                </div>
              )}
            </div>
          ) : (
            // Desktop: Table
            <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={menuItems.length > 0 && selectedProducts.length === menuItems.length}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 text-green-600 rounded"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sản phẩm</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Danh mục</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Giá bán</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Topping</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trạng thái</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Thao tác</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {menuItems.map((item) => (
                  <tr 
                    key={item._id} 
                    className={`hover:bg-gray-50 ${selectedProducts.includes(item._id) ? 'bg-blue-50' : ''}`}
                  >
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedProducts.includes(item._id)}
                        onChange={() => toggleProductSelection(item._id)}
                        className="w-4 h-4 text-green-600 rounded"
                      />
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center space-x-3">
                        <img 
                          src={item.image ? `${API_URL.replace('/api', '')}${item.image}` : '/default-product.png'} 
                          alt={item.name} 
                          className="w-12 h-12 rounded object-cover" 
                        />
                        <div>
                          <div className="font-semibold">{item.name}</div>
                          {item.customOptions && item.customOptions.length > 0 && (
                            <div className="text-xs text-blue-600">{item.customOptions.length} size</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="px-2 py-1 bg-gray-100 rounded text-sm">{item.category}</span>
                    </td>
                    <td className="px-4 py-4 font-semibold text-green-600">{formatCurrency(item.price)}</td>
                    <td className="px-4 py-4">
                      {item.availableToppings && item.availableToppings.length > 0 ? (
                        <span className="text-xs text-purple-600">{item.availableToppings.length} topping</span>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${item.available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {item.available ? 'Còn món' : 'Hết món'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <button onClick={() => openModal(item)} className="text-blue-600 hover:text-blue-800 mr-3"><FiEdit /></button>
                      <button onClick={() => deleteItem(item._id)} className="text-red-600 hover:text-red-800"><FiTrash2 /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {loadingMore && (
              <div className="text-center py-4 text-gray-500 text-sm">
                <div className="inline-block animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-green-500 mr-2"></div>
                Đang tải thêm...
              </div>
            )}
            {!loadingMore && hasMore && (
              <div ref={observerTarget} className="text-center py-2 text-gray-400 text-xs">
                Cuộn xuống để tải thêm ({menuItems.length} món đã tải)
              </div>
            )}
            
            {menuItems.length === 0 && !loading && (
              <div className="text-center py-12 text-gray-500">
                {searchTerm || filterCategory !== 'all' || filterStatus !== 'all' ? 'Không tìm thấy món ăn phù hợp' : 'Chưa có món ăn nào'}
              </div>
            )}
          </div>
          )}
        </>
      )}

      {/* Toppings Tab */}
      {activeTab === 'toppings' && (
        <>
          {/* Desktop: Add button */}
          {!isMobile && (
            <div className="flex justify-end">
              <button onClick={() => openToppingModal()} className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                <FiPlus /><span>Thêm topping</span>
              </button>
            </div>
          )}

          {/* Mobile: Search and Add button */}
          {isMobile && (
            <div className="bg-white px-4 py-3 flex items-center space-x-2">
              <div className="relative flex-1">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Tìm topping..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg"
                />
              </div>
              <button 
                onClick={() => openToppingModal()}
                className="p-2 rounded-lg bg-green-600 text-white hover:bg-green-700"
              >
                <FiPlus size={18} />
              </button>
            </div>
          )}

          {isMobile ? (
            // Mobile: Card list for toppings
            <div className="divide-y divide-gray-100">
              {toppings
                .filter(t => !searchTerm || t.name.toLowerCase().includes(searchTerm.toLowerCase()))
                .map((topping) => (
                <MobileToppingCard key={topping._id} topping={topping} />
              ))}
              {toppings.filter(t => !searchTerm || t.name.toLowerCase().includes(searchTerm.toLowerCase())).length === 0 && (
                <div className="text-center py-12 text-gray-500 text-sm">
                  {searchTerm ? 'Không tìm thấy topping phù hợp' : 'Chưa có topping nào'}
                </div>
              )}
            </div>
          ) : (
            // Desktop: Table
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tên topping</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Giá</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trạng thái</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tình trạng</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {toppings.map((topping) => (
                    <tr key={topping._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-semibold">{topping.name}</td>
                      <td className="px-6 py-4 text-green-600 font-semibold">{formatCurrency(topping.price)}</td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => toggleToppingActive(topping)}
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${topping.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}
                        >
                          {topping.active ? 'Đang bán' : 'Ngừng bán'}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => toggleToppingStock(topping)}
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${topping.inStock !== false ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'}`}
                        >
                          {topping.inStock !== false ? '✓ Còn hàng' : '✗ Hết hàng'}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button onClick={() => openToppingModal(topping)} className="text-blue-600 hover:text-blue-800 mr-3"><FiEdit /></button>
                        <button onClick={() => deleteTopping(topping._id)} className="text-red-600 hover:text-red-800"><FiTrash2 /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {toppings.length === 0 && (
                <div className="text-center py-12 text-gray-500">Chưa có topping nào</div>
              )}
            </div>
          )}
        </>
      )}

      {/* Product Modal */}
      {showModal && (
        <div className={`fixed inset-0 bg-black bg-opacity-50 z-[100] ${isMobile ? '' : 'flex items-center justify-center p-4'}`}>
          <div className={`bg-white ${isMobile ? 'fixed inset-0 overflow-y-auto' : 'rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto'}`}>
            {/* Header */}
            <div className={`flex justify-between items-center sticky top-0 bg-white z-10 ${isMobile ? 'px-4 py-3 border-b shadow-sm' : 'mb-4'}`}>
              {isMobile && (
                <button onClick={closeModal} className="p-1 -ml-1">
                  <FiChevronLeft size={24} />
                </button>
              )}
              <h2 className={`font-bold ${isMobile ? 'text-lg flex-1 text-center' : 'text-xl'}`}>
                {editingItem ? 'Sửa món' : 'Thêm món mới'}
              </h2>
              {isMobile ? (
                <button 
                  type="button"
                  onClick={() => document.getElementById('product-form').requestSubmit()}
                  className="text-green-600 font-semibold text-sm"
                >
                  Lưu
                </button>
              ) : (
                <button onClick={closeModal}><FiX size={24} /></button>
              )}
            </div>
            
            <form id="product-form" onSubmit={handleSubmit} className={`space-y-4 ${isMobile ? 'p-4 pb-24' : ''}`}>
              {/* Image - Mobile optimized */}
              {isMobile ? (
                <div>
                  <label className="block text-sm font-semibold mb-1">Hình ảnh món</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-3">
                    {imagePreview ? (
                      <div className="relative">
                        <img src={imagePreview} alt="Preview" className="w-full h-32 object-cover rounded" />
                        {/* Action buttons overlay */}
                        <div className="absolute top-2 right-2 flex flex-col space-y-1">
                          <button type="button" onClick={() => { setImagePreview(null); setImageFile(null); }} className="bg-red-500 text-white rounded-full p-1.5">
                            <FiX size={14} />
                          </button>
                          <label htmlFor="image-upload" className="bg-blue-500 text-white rounded-full p-1.5 cursor-pointer">
                            <FiImage size={14} />
                          </label>
                        </div>
                      </div>
                    ) : (
                      <label htmlFor="image-upload" className="flex items-center justify-center space-x-3 cursor-pointer py-4">
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                          <FiImage className="text-gray-400" size={24} />
                        </div>
                        <div className="text-left">
                          <p className="text-sm text-gray-600">Thêm ảnh món</p>
                          <p className="text-xs text-gray-400">PNG, JPG (max 5MB)</p>
                        </div>
                      </label>
                    )}
                    <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" id="image-upload" />
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block font-semibold mb-2">Hình ảnh món</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                    {imagePreview ? (
                      <div className="relative">
                        <img src={imagePreview} alt="Preview" className="w-full h-48 object-cover rounded" />
                        <button type="button" onClick={() => { setImagePreview(null); setImageFile(null); }} className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"><FiX size={16} /></button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center space-x-3">
                        <FiImage className="text-gray-400" size={48} />
                        <div className="text-left">
                          <p className="text-sm text-gray-600">Chọn ảnh món</p>
                          <p className="text-gray-400 text-xs">PNG, JPG (max 5MB)</p>
                        </div>
                      </div>
                    )}
                    <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" id="image-upload" />
                    <label htmlFor="image-upload" className="cursor-pointer inline-block mt-2 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
                      {imagePreview ? 'Đổi ảnh' : 'Chọn ảnh'}
                    </label>
                  </div>
                </div>
              )}
              
              {/* Mobile: Name full width, Price & Category side by side */}
              {isMobile ? (
                <>
                  <div>
                    <label className="block text-sm font-semibold mb-1">Tên món *</label>
                    <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm" required placeholder="VD: Cà phê sữa đá" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-semibold mb-1">Giá (VND) *</label>
                      <input type="number" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} onWheel={(e) => e.target.blur()} className="w-full border rounded-lg px-3 py-2 text-sm" required placeholder="25000" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-1">Danh mục *</label>
                      <select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm" required>
                        <option value="">Chọn...</option>
                        {categories.map(cat => (
                          <option key={cat._id} value={cat.name}>{cat.icon} {cat.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block font-semibold mb-2">Tên món *</label>
                    <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full border rounded-lg px-4 py-2" required />
                  </div>
                  <div>
                    <label className="block font-semibold mb-2">Giá cơ bản (VND) *</label>
                    <input type="number" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} onWheel={(e) => e.target.blur()} className="w-full border rounded-lg px-4 py-2" required />
                  </div>
                  {/* Category - Desktop */}
                  <div>
                    <label className="block font-semibold mb-2">Danh mục *</label>
                    <select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} className="w-full border rounded-lg px-4 py-2" required>
                      <option value="">-- Chọn danh mục --</option>
                      {categories.map(cat => (
                        <option key={cat._id} value={cat.name}>{cat.icon} {cat.name}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              
              {/* Description - Smaller on mobile */}
              <div>
                <label className={`block font-semibold ${isMobile ? 'text-sm mb-1' : 'mb-2'}`}>Mô tả</label>
                <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className={`w-full border rounded-lg ${isMobile ? 'px-3 py-2 text-sm' : 'px-4 py-2'}`} rows={isMobile ? 2 : 3} placeholder="Mô tả ngắn về món..." />
              </div>
              
              {/* Options checkboxes - Inline on mobile */}
              <div className={`flex ${isMobile ? 'flex-wrap gap-4' : 'items-center space-x-6'}`}>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input type="checkbox" checked={formData.available} onChange={(e) => setFormData({...formData, available: e.target.checked})} className="w-4 h-4 rounded" />
                  <span className={isMobile ? 'text-sm' : ''}>Còn món</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input type="checkbox" checked={formData.hasHotOption} onChange={(e) => setFormData({...formData, hasHotOption: e.target.checked})} className="w-4 h-4 rounded" />
                  <span className={isMobile ? 'text-sm' : ''}>🔥 Có đồ nóng</span>
                </label>
              </div>

              {/* Custom Options (Size) - Collapsible on mobile */}
              <div className={`border-t pt-4 ${isMobile ? 'mt-2' : ''}`}>
                <label className={`block font-semibold ${isMobile ? 'text-sm mb-1' : 'mb-2'}`}>Size (Tùy chọn)</label>
                <p className={`text-gray-600 mb-2 ${isMobile ? 'text-xs' : 'text-sm mb-3'}`}>VD: Size L (+10,000đ)</p>
                
                {customOptions.length > 0 && (
                  <div className="mb-2 space-y-1">
                    {customOptions.map((opt, idx) => (
                      <div key={idx} className={`flex items-center justify-between bg-blue-50 rounded ${isMobile ? 'px-2 py-1.5' : 'p-3'}`}>
                        <div className={isMobile ? 'text-sm' : ''}>
                          <span className="font-semibold">{opt.name}</span>
                          <span className="text-green-600 ml-2">+{formatCurrency(opt.price)}</span>
                        </div>
                        <button type="button" onClick={() => removeCustomOption(idx)} className="text-red-500 p-1"><FiX size={16} /></button>
                      </div>
                    ))}
                  </div>
                )}

                <div className={`flex ${isMobile ? 'space-x-1' : 'space-x-2'}`}>
                  <input type="text" placeholder="Tên size" value={newOption.name} onChange={(e) => setNewOption({...newOption, name: e.target.value})} className={`flex-1 border rounded-lg ${isMobile ? 'px-2 py-1.5 text-sm' : 'px-3 py-2'}`} />
                  <input type="number" placeholder="Giá" value={newOption.price} onChange={(e) => setNewOption({...newOption, price: e.target.value})} onWheel={(e) => e.target.blur()} className={`border rounded-lg ${isMobile ? 'w-20 px-2 py-1.5 text-sm' : 'w-32 px-3 py-2'}`} />
                  <button type="button" onClick={addCustomOption} className={`bg-blue-600 text-white rounded-lg hover:bg-blue-700 ${isMobile ? 'px-2' : 'px-4 py-2'}`}><FiPlus size={isMobile ? 16 : 20} /></button>
                </div>
              </div>

              {/* Topping Selection - Compact grid on mobile */}
              {toppings.filter(t => t.active).length > 0 && (
                <div className="border-t pt-4">
                  <label className={`block font-semibold ${isMobile ? 'text-sm mb-1' : 'mb-2'}`}>🧋 Topping có thể chọn</label>
                  <p className={`text-gray-600 ${isMobile ? 'text-xs mb-2' : 'text-sm mb-3'}`}>Chọn topping khách có thể thêm</p>
                  <div className={`grid gap-2 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                    {toppings.filter(t => t.active).map(topping => (
                      <label 
                        key={topping._id} 
                        className={`flex items-center justify-between border rounded-lg cursor-pointer transition-all ${
                          selectedToppings.includes(topping._id) ? 'bg-purple-50 border-purple-500' : 'hover:bg-gray-50'
                        } ${isMobile ? 'p-2' : 'p-3'}`}
                      >
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={selectedToppings.includes(topping._id)}
                            onChange={() => toggleToppingSelection(topping._id)}
                            className="w-4 h-4 text-purple-600"
                          />
                          <span className={`font-medium ${isMobile ? 'text-sm' : ''}`}>{topping.name}</span>
                        </div>
                        <span className={`text-green-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>+{formatCurrency(topping.price)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Delete button for editing - Mobile only */}
              {isMobile && editingItem && (
                <button 
                  type="button" 
                  onClick={() => {
                    if (window.confirm('Xóa món này?')) {
                      deleteItem(editingItem._id);
                      closeModal();
                    }
                  }}
                  className="w-full py-2.5 text-red-600 font-medium border border-red-200 rounded-lg mt-4"
                >
                  Xóa món này
                </button>
              )}
              
              {/* Submit button - Desktop only */}
              {!isMobile && (
                <button type="submit" className="w-full bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700">
                  {editingItem ? 'Cập nhật' : 'Thêm món'}
                </button>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Topping Modal */}
      {showToppingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{editingTopping ? 'Sửa topping' : 'Thêm topping mới'}</h2>
              <button onClick={closeToppingModal}><FiX size={24} /></button>
            </div>
            <form onSubmit={handleToppingSubmit} className="space-y-4">
              <div>
                <label className="block font-semibold mb-2">Tên topping *</label>
                <input 
                  type="text" 
                  value={toppingForm.name} 
                  onChange={(e) => setToppingForm({...toppingForm, name: e.target.value})} 
                  className="w-full border rounded-lg px-4 py-2" 
                  placeholder="VD: Trân châu đen"
                  required 
                />
              </div>
              <div>
                <label className="block font-semibold mb-2">Giá (VND) *</label>
                <input 
                  type="number" 
                  value={toppingForm.price} 
                  onChange={(e) => setToppingForm({...toppingForm, price: e.target.value})} 
                  onWheel={(e) => e.target.blur()}
                  className="w-full border rounded-lg px-4 py-2" 
                  placeholder="VD: 10000"
                  required 
                />
              </div>
              <button type="submit" className="w-full bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700">
                {editingTopping ? 'Cập nhật' : 'Thêm topping'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Add Topping Modal */}
      {showBulkToppingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">🧋 Thêm topping cho {selectedProducts.length} sản phẩm</h2>
              <button onClick={closeBulkToppingModal}><FiX size={24} /></button>
            </div>
            
            <p className="text-gray-600 mb-4">
              Chọn các topping muốn thêm vào {selectedProducts.length} sản phẩm đã chọn. 
              Topping sẽ được thêm vào danh sách topping hiện có của mỗi sản phẩm.
            </p>

            {toppings.filter(t => t.active).length > 0 ? (
              <div className="space-y-2 mb-6">
                {toppings.filter(t => t.active).map(topping => (
                  <label 
                    key={topping._id} 
                    className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-all ${bulkToppings.includes(topping._id) ? 'bg-purple-50 border-purple-500' : 'hover:bg-gray-50'}`}
                  >
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={bulkToppings.includes(topping._id)}
                        onChange={() => toggleBulkTopping(topping._id)}
                        className="w-5 h-5 text-purple-600 rounded"
                      />
                      <span className="font-semibold">{topping.name}</span>
                    </div>
                    <span className="text-green-600 font-semibold">+{formatCurrency(topping.price)}</span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Chưa có topping nào. Vui lòng thêm topping trước.
              </div>
            )}

            <div className="flex space-x-3">
              <button
                onClick={closeBulkToppingModal}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                onClick={handleBulkAddToppings}
                disabled={bulkToppings.length === 0}
                className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Thêm {bulkToppings.length} topping
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Edit Modal */}
      {showBulkEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">✏️ Sửa hàng loạt {selectedProducts.length} sản phẩm</h2>
              <button onClick={closeBulkEditModal}><FiX size={24} /></button>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>💡 Hướng dẫn:</strong> Chỉ các trường có giá trị mới được cập nhật. 
                Các trường để trống hoặc chọn "Không thay đổi" sẽ giữ nguyên giá trị hiện tại của từng sản phẩm.
              </p>
            </div>

            <div className="space-y-6">
              {/* Description */}
              <div>
                <label className="block font-semibold mb-2">Mô tả</label>
                <textarea
                  value={bulkEditForm.description}
                  onChange={(e) => setBulkEditForm({...bulkEditForm, description: e.target.value})}
                  className="w-full border rounded-lg px-4 py-3"
                  placeholder="Để trống = không thay đổi"
                  rows={3}
                />
              </div>

              {/* Category */}
              <div>
                <label className="block font-semibold mb-2">Danh mục</label>
                <select
                  value={bulkEditForm.category}
                  onChange={(e) => setBulkEditForm({...bulkEditForm, category: e.target.value})}
                  className="w-full border rounded-lg px-4 py-3"
                >
                  <option value="">-- Không thay đổi --</option>
                  {categories.map(cat => (
                    <option key={cat._id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {/* Price */}
              <div>
                <label className="block font-semibold mb-2">Giá</label>
                <input
                  type="number"
                  value={bulkEditForm.price}
                  onChange={(e) => setBulkEditForm({...bulkEditForm, price: e.target.value})}
                  onWheel={(e) => e.target.blur()}
                  className="w-full border rounded-lg px-4 py-3"
                  placeholder="Để trống = không thay đổi"
                  min="0"
                />
              </div>

              {/* Available Status */}
              <div>
                <label className="block font-semibold mb-2">Trạng thái</label>
                <select
                  value={bulkEditForm.available}
                  onChange={(e) => setBulkEditForm({...bulkEditForm, available: e.target.value})}
                  className="w-full border rounded-lg px-4 py-3"
                >
                  <option value="">-- Không thay đổi --</option>
                  <option value="true">Còn bán</option>
                  <option value="false">Ngừng bán</option>
                </select>
              </div>

              {/* Has Hot Option */}
              <div>
                <label className="block font-semibold mb-2">🔥 Có đồ nóng</label>
                <select
                  value={bulkEditForm.hasHotOption}
                  onChange={(e) => setBulkEditForm({...bulkEditForm, hasHotOption: e.target.value})}
                  className="w-full border rounded-lg px-4 py-3"
                >
                  <option value="">-- Không thay đổi --</option>
                  <option value="true">Có đồ nóng</option>
                  <option value="false">Không có đồ nóng</option>
                </select>
              </div>

              {/* Topping Action */}
              <div>
                <label className="block font-semibold mb-2">Topping</label>
                <select
                  value={bulkEditForm.toppingAction}
                  onChange={(e) => setBulkEditForm({...bulkEditForm, toppingAction: e.target.value, selectedToppings: []})}
                  className="w-full border rounded-lg px-4 py-3 mb-3"
                >
                  <option value="">-- Không thay đổi --</option>
                  <option value="add">Thêm vào danh sách hiện có</option>
                  <option value="replace">Thay thế toàn bộ</option>
                  <option value="remove">Xóa tất cả topping</option>
                </select>

                {/* Topping Selection - show when action is add or replace */}
                {(bulkEditForm.toppingAction === 'add' || bulkEditForm.toppingAction === 'replace') && (
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <p className="text-sm text-gray-600 mb-3">
                      {bulkEditForm.toppingAction === 'add' 
                        ? 'Chọn topping để thêm vào:' 
                        : 'Chọn topping thay thế:'}
                    </p>
                    {toppings.filter(t => t.active).length > 0 ? (
                      <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                        {toppings.filter(t => t.active).map(topping => (
                          <label 
                            key={topping._id} 
                            className={`flex items-center space-x-2 p-2 border rounded cursor-pointer transition-all ${bulkEditForm.selectedToppings.includes(topping._id) ? 'bg-purple-100 border-purple-500' : 'hover:bg-gray-100'}`}
                          >
                            <input
                              type="checkbox"
                              checked={bulkEditForm.selectedToppings.includes(topping._id)}
                              onChange={() => toggleBulkEditTopping(topping._id)}
                              className="w-4 h-4 text-purple-600 rounded"
                            />
                            <span className="text-sm">{topping.name}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">Chưa có topping nào</p>
                    )}
                  </div>
                )}

                {bulkEditForm.toppingAction === 'remove' && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <p className="text-sm text-orange-800">
                      ⚠️ Tất cả topping của {selectedProducts.length} sản phẩm sẽ bị xóa.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Summary */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold mb-2">Tóm tắt thay đổi:</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                {bulkEditForm.description && <li>• Mô tả: <span className="font-medium">{bulkEditForm.description.length > 50 ? bulkEditForm.description.substring(0, 50) + '...' : bulkEditForm.description}</span></li>}
                {bulkEditForm.category && <li>• Danh mục: <span className="font-medium">{bulkEditForm.category}</span></li>}
                {bulkEditForm.price && <li>• Giá: <span className="font-medium">{formatCurrency(Number(bulkEditForm.price))}</span></li>}
                {bulkEditForm.available && <li>• Trạng thái: <span className="font-medium">{bulkEditForm.available === 'true' ? 'Còn bán' : 'Ngừng bán'}</span></li>}
                {bulkEditForm.hasHotOption && <li>• Có đồ nóng: <span className="font-medium">{bulkEditForm.hasHotOption === 'true' ? 'Có' : 'Không'}</span></li>}
                {bulkEditForm.toppingAction === 'add' && bulkEditForm.selectedToppings.length > 0 && (
                  <li>• Thêm {bulkEditForm.selectedToppings.length} topping</li>
                )}
                {bulkEditForm.toppingAction === 'replace' && bulkEditForm.selectedToppings.length > 0 && (
                  <li>• Thay thế bằng {bulkEditForm.selectedToppings.length} topping</li>
                )}
                {bulkEditForm.toppingAction === 'remove' && <li>• Xóa tất cả topping</li>}
                {!bulkEditForm.description && !bulkEditForm.category && !bulkEditForm.price && !bulkEditForm.available && !bulkEditForm.hasHotOption && !bulkEditForm.toppingAction && (
                  <li className="text-gray-400 italic">Chưa có thay đổi nào</li>
                )}
              </ul>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={closeBulkEditModal}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                onClick={handleBulkEdit}
                disabled={!bulkEditForm.description && !bulkEditForm.category && !bulkEditForm.price && !bulkEditForm.available && !bulkEditForm.hasHotOption && !bulkEditForm.toppingAction}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cập nhật {selectedProducts.length} sản phẩm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MenuManagement;
